import React from 'react';
import { ArrowLeft, Calendar, Clock, MapPin, User, Users, Video } from 'lucide-react';
import { useI18n } from '../../utils/i18n';
import './mobil.css';

// Kalendertermin ohne Ticket in der Mobilansicht. Inhaltlich wie der
// Desktop-Dialog CalendarEventDetail, aber im Vollbild und mit großen Knöpfen.
// Der Dialog selbst ist für die Handybreite zu breit.

const parseUtc = (dateTime) => new Date(String(dateTime || '').endsWith('Z') ? dateTime : `${dateTime}Z`);

// Termintext als reiner Text: Tags weg, HTML-Entitäten dekodiert (DOMParser
// führt keine Skripte aus), Leerzeilen zusammengezogen und die Trennlinie aus
// Teams-Einladungen (_____) entfernt. Eigenes HTML wird nie gerendert.
export function terminText(html) {
  try {
    const roh = String(html || '');
    if (!roh.trim()) return '';
    // <style>/<script>: textContent würde ihren Inhalt als Text mitnehmen
    const mitUmbruechen = roh
      .replace(/<(style|script)\b[\s\S]*?<\/\1>/gi, '')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|li|tr|h\d)>/gi, '\n');
    const text = new DOMParser().parseFromString(mitUmbruechen, 'text/html').body.textContent || '';
    return text
      .replace(/\u00a0/g, ' ')
      .split('\n')
      .map(zeile => zeile.trim())
      .filter(zeile => !/^_{5,}$/.test(zeile))
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  } catch (_) {
    return '';
  }
}

// Teams-Link wie in CalendarEventDetail erkennen
export function teamsLink(event) {
  const provider = String(event?.onlineMeetingProvider || '').toLowerCase();
  const kandidat = event?.onlineMeeting?.joinUrl || event?.onlineMeetingUrl || null;
  const istTeams = Boolean(
    (event?.isOnlineMeeting && provider.includes('teams')) ||
    (kandidat && /https?:\/\/(.*\.)?teams\.microsoft\.com/i.test(kandidat))
  );
  return istTeams ? kandidat : null;
}

const MobilTermin = ({ event, onClose }) => {
  const { t, getLocale } = useI18n();
  if (!event) return null;

  const start = parseUtc(event?.start?.dateTime);
  const ende = parseUtc(event?.end?.dateTime);
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const datum = (() => {
    try { return start.toLocaleDateString(getLocale(), { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric', timeZone: zone }); } catch (_) { return ''; }
  })();
  const uhr = (d) => {
    try { return d.toLocaleTimeString(getLocale(), { hour: '2-digit', minute: '2-digit', timeZone: zone }); } catch (_) { return ''; }
  };
  const status = {
    busy: t('eventDetail.busy', 'Beschäftigt'),
    tentative: t('eventDetail.tentative', 'Vorläufig'),
    free: t('eventDetail.free', 'Frei'),
    oof: t('eventDetail.oof', 'Abwesend')
  }[event?.showAs] || t('eventDetail.appointment', 'Termin');

  const betreff = event?.subject || t('eventDetail.noSubject', '(Kein Betreff)');
  const ort = event?.location?.displayName || '';
  const organisator = event?.organizer?.emailAddress?.name || event?.organizer?.emailAddress?.address || '';
  const teilnehmer = (Array.isArray(event?.attendees) ? event.attendees : [])
    .map(a => a?.emailAddress?.name || a?.emailAddress?.address)
    .filter(Boolean);
  const link = teamsLink(event);
  const text = terminText(event?.body?.content);

  return (
    <div className="mobil-detail" role="dialog" aria-modal="true" aria-label={betreff}>
      <header className="mobil-kopf">
        <button type="button" className="mobil-kopf-knopf" onClick={onClose}
          aria-label={t('mobile.back', 'Zurück')} title={t('mobile.back', 'Zurück')}>
          <ArrowLeft size={24} />
        </button>
        <h1>{t('eventDetail.header', 'Termin')}</h1>
      </header>

      <div className="mobil-detail-inhalt">
        <section className="mobil-karte">
          <h2 className="mobil-titel">{betreff}</h2>
          <div className="mobil-chips">
            <span className="mobil-chip"><Calendar size={14} aria-hidden="true" />{status}</span>
          </div>
          <ul className="mobil-termin-daten">
            <li><Calendar size={18} aria-hidden="true" /><span>{datum}</span></li>
            <li><Clock size={18} aria-hidden="true" /><span>{uhr(start)} – {uhr(ende)}</span></li>
            {ort && <li><MapPin size={18} aria-hidden="true" /><span>{ort}</span></li>}
            {organisator && <li><User size={18} aria-hidden="true" /><span>{organisator}</span></li>}
            {teilnehmer.length > 0 && <li><Users size={18} aria-hidden="true" /><span>{teilnehmer.join(', ')}</span></li>}
          </ul>
        </section>

        {link && (
          <a href={link} target="_blank" rel="noreferrer" className="btn btn-primary mobil-touch mobil-termin-beitreten">
            <Video size={20} aria-hidden="true" /> {t('eventDetail.joinMeeting', 'Besprechung beitreten')}
          </a>
        )}

        {text && (
          <section className="mobil-karte">
            <h3 className="mobil-label">{t('mobile.eventNotes', 'Notizen')}</h3>
            <div className="mobil-beschreibung">{text}</div>
          </section>
        )}
      </div>
    </div>
  );
};

export default MobilTermin;
